/**
 * AppRouter.tsx
 * Configuration du routing React Router
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { App } from '../App';
import { AdminPanel } from '../components/admin/AdminPanel';
import { TemplateDashboard } from '../components/react/TemplateDashboard/TemplateDashboard';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { LoginPage } from '../pages/LoginPage';
import { RegisterPage } from '../pages/RegisterPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { KioskView } from '../pages/KioskView';
import { LiveView } from '../pages/LiveView';
import { VariationsView } from '../pages/VariationsView';
import {
  CommandPalette,
  useCommandPaletteGlobalShortcut,
} from '../components/react/CommandPalette/CommandPalette';
import { SnapboothNav } from '../components/react/common/SnapboothNav';

/**
 * Layout partagé pour les routes protégées : rend l'Outlet ET la palette ⌘K.
 * La palette est montée UNE seule fois ici, au-dessus de toutes les pages
 * protégées (dashboard, editor, etc.) — pour réagir à ⌘K partout sans
 * dupliquer le hook dans chaque vue.
 */
const ProtectedLayout: React.FC = () => {
  const { open, setOpen } = useCommandPaletteGlobalShortcut();
  const location = useLocation();
  // FAB et meta-nav visibles partout SAUF sur le kiosque (plein écran invité)
  const isKiosk = location.pathname.startsWith('/kiosk');

  return (
    <>
      {/* Meta-nav globale (Dashboard / Éditeur / Live / Kiosque / Variations) */}
      {!isKiosk && (
        <div
          className="sb-app-shell"
          style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
        >
          <SnapboothNav />
          <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'auto' }}>
            <Outlet />
          </div>
        </div>
      )}
      {isKiosk && <Outlet />}

      <CommandPalette open={open} onClose={() => setOpen(false)} />
      {!isKiosk && (
        <button
          type="button"
          className="sb-help-fab"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir la palette de commandes (⌘K)"
          title="Palette de commandes (⌘K)"
        >
          <Search size={16} />
          <span>Commandes</span>
          <kbd>⌘K</kbd>
        </button>
      )}
    </>
  );
};

/**
 * Routeur principal de l'application
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Routes publiques */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Routes protégées avec layout partagé */}
        <Route
          element={
            <ProtectedRoute>
              <ProtectedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/editor" replace />} />
          <Route path="/editor" element={<App />} />
          <Route path="/editor/:projectId" element={<App />} />

          {/* Route Dashboard - Protégée */}
          <Route path="/dashboard" element={<TemplateDashboard />} />

          {/* Kiosk + Live view — vues événement (Snapbooth Redesign) */}
          <Route path="/kiosk" element={<KioskView />} />
          <Route path="/live" element={<LiveView />} />
          <Route path="/variations" element={<VariationsView />} />
        </Route>

        {/* Route Admin - Protégée */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminPanel />
            </ProtectedRoute>
          }
        />

        {/* 404 - aucune route ne correspond */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};
